import { useState } from "react";
import { motion } from "framer-motion";
import { reveal, EASE } from "@/lib/motion";
import MaskLines from "@/components/MaskLines";
import { formatNL } from "@/lib/calendar";

const PHONE_DISPLAY = "06 1376 7661";
const PHONE_LINK = "tel:+31613767661";
const EMAIL_LINK = "mailto:info@blankwaters.nl";
const WHATSAPP_LINK = "https://wa.me/31613767661";

const METHODS = [
  { id: "contact-tel", href: PHONE_LINK, label: "Bellen", value: PHONE_DISPLAY, external: false },
  { id: "contact-email", href: EMAIL_LINK, label: "E-mail", value: "info@blankwaters.nl", external: false },
  { id: "contact-whatsapp", href: WHATSAPP_LINK, label: "WhatsApp", value: "Stuur je klus en foto’s", external: true },
];

function buildLinks({ place, room, access, message, preferredDate }) {
  const subject = `Vrijblijvende offerteaanvraag | ${place}`;
  const body = [
    "Hallo Boris en Timo,",
    "",
    "Ik ontvang graag een vrijblijvende offerte voor het uitruimen van mijn ruimte.",
    "",
    `Plaats: ${place}`,
    `Ruimte: ${room}`,
    `Bereikbaarheid: ${access}`,
    ...(preferredDate ? [`Voorkeursdatum: ${preferredDate}`] : []),
    `Omschrijving: ${message}`,
    "",
    "Ik voeg zo nodig foto's toe aan dit bericht.",
    "",
    "Met vriendelijke groet,",
  ].join(" ");
  return {
    email: `${EMAIL_LINK}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    whatsapp: `${WHATSAPP_LINK}?text=${encodeURIComponent(body)}`,
  };
}

export default function Contact({ preferredDate, onClearDate }) {
  const [error, setError] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const place = String(data.get("place") || "").trim();
    const message = String(data.get("message") || "").trim();
    if (!place || message.length < 10) {
      setError("Vul je plaats in en beschrijf je klus in minimaal 10 tekens.");
      return;
    }
    setError("");
    const links = buildLinks({
      place,
      room: String(data.get("room") || ""),
      access: String(data.get("access") || ""),
      message,
      preferredDate,
    });
    const channel = e.nativeEvent.submitter?.value === "whatsapp" ? "whatsapp" : "email";
    if (channel === "whatsapp") {
      window.open(links.whatsapp, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = links.email;
    }
  };

  return (
    <section id="offerte" className="section contact" data-testid="offerte">
      <motion.div {...reveal()}>
        <p className="eyebrow">VRAAG VRIJBLIJVEND EEN OFFERTE AAN</p>
        <MaskLines lines={["Maak plaats", "voor je plannen."]} testId="offerte-titel" />
        <p>
          Vertel ons welke ruimte je wilt uitruimen. Bel, mail of stuur een bericht met een paar
          overzichtsfoto’s.
        </p>
        <div className="contact-links">
          {METHODS.map((m, i) => (
            <motion.a
              key={m.id}
              className="contact-method"
              href={m.href}
              {...(m.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              data-testid={m.id}
              initial={{ opacity: 0, x: -18 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
            >
              <span>{m.label}</span>
              <strong>{m.value}</strong>
              <span aria-hidden="true">↗</span>
            </motion.a>
          ))}
        </div>
        <p className="contact-note">
          Een bericht is een vrijblijvende aanvraag. We spreken de prijs en uitvoering samen af.
        </p>
      </motion.div>
      <motion.form onSubmit={onSubmit} {...reveal(0.08)} data-testid="quote-form">
        <label htmlFor="place">Plaats</label>
        <input
          id="place"
          required
          maxLength={100}
          placeholder="Bijvoorbeeld Mariahout"
          name="place"
          data-testid="quote-place-input"
        />
        <div className="form-row">
          <div>
            <label htmlFor="room">Welke ruimte?</label>
            <select id="room" name="room" data-testid="quote-room-select">
              <option>Garage</option>
              <option>Zolder</option>
              <option>Schuur</option>
              <option>Berging</option>
              <option>Meerdere ruimtes</option>
            </select>
          </div>
          <div>
            <label htmlFor="access">Bereikbaarheid</label>
            <select id="access" name="access" data-testid="quote-access-select">
              <option>Begane grond</option>
              <option>Via een vaste trap</option>
              <option>Via een zoldertrap</option>
              <option>Nog bespreken</option>
            </select>
          </div>
        </div>
        <label htmlFor="message">Wat wil je laten uitruimen?</label>
        <textarea
          id="message"
          name="message"
          required
          minLength={10}
          maxLength={2000}
          rows={5}
          placeholder="Wat moet blijven, wat mag weg en hoeveel spullen zijn het?"
          data-testid="quote-message-textarea"
        />
        {preferredDate ? (
          <div className="date-chip" data-testid="date-chip">
            Voorkeursdatum: {formatNL(preferredDate)}
            <button type="button" onClick={onClearDate} aria-label="Voorkeursdatum wissen" data-testid="date-chip-clear">
              ×
            </button>
          </div>
        ) : null}
        {error ? (
          <p className="form-error" role="alert" data-testid="quote-form-error">
            {error}
          </p>
        ) : null}
        <p className="form-help">
          Kies e-mail of WhatsApp. Je bericht wordt daar klaargezet; voeg eventueel foto’s toe en
          druk zelf op Verzenden.
        </p>
        <button type="submit" value="email" className="contact-ticket" name="channel" data-testid="quote-submit-email">
          Offerte aanvragen via e-mail<span aria-hidden="true">↗</span>
        </button>
        <button type="submit" value="whatsapp" className="contact-whatsapp" name="channel" data-testid="quote-submit-whatsapp">
          Liever via WhatsApp<span aria-hidden="true">↗</span>
        </button>
      </motion.form>
    </section>
  );
}
